// Loads the Rust crypto core (crates/hrth-wasm, built by `make wasm`) and gives it types.
import init, {
  HrthCore as RawCore,
  initSync,
  newConversationId,
  nodeAnswerKeypair,
  nodeAnswerOpen,
  recoveryGenerate,
  recoveryProof,
  recoveryVerifier,
  s3Presign,
} from '../wasm/pkg/hrth.js'
import type { CommitRequest, DeviceRef, Envelope, LogEntry, MembershipDelta, UploadKeyPackagesRequest } from '../api/types'

let ready: Promise<void> | null = null

/** Initializes the WASM module once (browser: fetches the .wasm next to the JS). */
export function loadCore(): Promise<void> {
  ready ??= init().then(() => undefined)
  return ready
}

/** Synchronous init from bytes (Node/Vitest). */
export function loadCoreFromBytes(bytes: BufferSource): void {
  initSync({ module: bytes })
  ready = Promise.resolve()
}

export type Incoming =
  | { kind: 'message'; sender: DeviceRef; envelope: Envelope }
  | { kind: 'undecodable'; sender: DeviceRef }
  | { kind: 'commit'; sender: DeviceRef | null; actual: MembershipDelta; delta_mismatch: boolean; removed_self: boolean; resync: boolean }
  | { kind: 'skipped' }

export interface Encrypted {
  epoch: number
  message: string
  envelope: Envelope
  /** Franking commitment for the server (reportable messages, HRTH-94). */
  commitment?: string
}

/** Error thrown by the core; `code` is e.g. `unknown_group`, `group_exists`, `mls`. */
export class CoreError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'CoreError'
  }
}

function wrap<T>(f: () => T): T {
  try {
    return f()
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    const idx = msg.indexOf(':')
    throw new CoreError(idx > 0 ? msg.slice(0, idx) : 'unknown', msg)
  }
}

/** Typed facade over the wasm-bindgen class. */
export class Core {
  private constructor(private raw: RawCore) {}

  static generate(userId: string): Core {
    return new Core(wrap(() => RawCore.generate(userId)))
  }

  static restore(snapshot: Uint8Array): Core {
    return new Core(wrap(() => RawCore.restore(snapshot)))
  }

  snapshot(): Uint8Array {
    return this.raw.snapshot()
  }
  get userId(): string {
    return this.raw.userId()
  }
  get deviceId(): string {
    return this.raw.deviceId()
  }
  get publicKey(): string {
    return this.raw.publicKey()
  }
  registerSignature(): string {
    return this.raw.registerSignature()
  }
  loginSignature(nonce: string): string {
    return wrap(() => this.raw.loginSignature(nonce))
  }
  keyPackageUpload(n: number, includeLastResort: boolean): UploadKeyPackagesRequest {
    return wrap(() => this.raw.keyPackageUpload(n, includeLastResort)) as UploadKeyPackagesRequest
  }
  conversations(): string[] {
    return this.raw.conversations()
  }
  hasGroup(conv: string): boolean {
    return wrap(() => this.raw.hasGroup(conv))
  }
  epoch(conv: string): number {
    return wrap(() => this.raw.epoch(conv))
  }
  members(conv: string): DeviceRef[] {
    return wrap(() => this.raw.members(conv)) as DeviceRef[]
  }
  callKey(conv: string): { epoch: number; key: string } {
    return wrap(() => this.raw.callKey(conv)) as { epoch: number; key: string }
  }
  epochAuthenticator(conv: string): string {
    return wrap(() => this.raw.epochAuthenticator(conv))
  }
  createGroup(conv: string): string {
    return wrap(() => this.raw.createGroup(conv))
  }
  addMembers(conv: string, keyPackages: string[]): CommitRequest {
    return wrap(() => this.raw.addMembers(conv, keyPackages)) as CommitRequest
  }
  removeDevices(conv: string, devices: DeviceRef[]): CommitRequest {
    return wrap(() => this.raw.removeDevices(conv, devices)) as CommitRequest
  }
  updateKeys(conv: string): CommitRequest {
    return wrap(() => this.raw.updateKeys(conv)) as CommitRequest
  }
  confirmCommit(conv: string): void {
    wrap(() => this.raw.confirmCommit(conv))
  }
  abortCommit(conv: string): void {
    wrap(() => this.raw.abortCommit(conv))
  }
  joinExternal(conv: string, groupInfo: string): CommitRequest {
    return wrap(() => this.raw.joinExternal(conv, groupInfo)) as CommitRequest
  }
  forgetGroup(conv: string): void {
    wrap(() => this.raw.forgetGroup(conv))
  }
  joinWelcome(welcome: string): string {
    return wrap(() => this.raw.joinWelcome(welcome))
  }
  encryptText(conv: string, text: string, replyTo?: string): Encrypted {
    return wrap(() => this.raw.encryptText(conv, text, Date.now(), replyTo)) as Encrypted
  }
  encrypt(conv: string, envelope: Envelope): Encrypted {
    return wrap(() => this.raw.encrypt(conv, envelope)) as Encrypted
  }
  process(conv: string, entry: LogEntry): Incoming {
    return wrap(() => this.raw.process(conv, entry)) as Incoming
  }
  unauthorizedDevices(conv: string, authorized: DeviceRef[]): DeviceRef[] {
    return wrap(() => this.raw.unauthorizedDevices(conv, authorized)) as DeviceRef[]
  }
  unauthorizedMembers(conv: string, authorized: string[]): DeviceRef[] {
    return wrap(() => this.raw.unauthorizedMembers(conv, authorized)) as DeviceRef[]
  }
  free(): void {
    this.raw.free()
  }
}

export { newConversationId, nodeAnswerKeypair, nodeAnswerOpen, s3Presign }

export const recovery = {
  generate: (): string => recoveryGenerate(),
  proof: (code: string): string | undefined => recoveryProof(code),
  verifier: (proof: string): string => wrap(() => recoveryVerifier(proof)),
}
